import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  FileUp,
  Loader2,
  ShieldCheck,
  Upload,
  XCircle,
} from "lucide-react";
import {
  parseMobileconfig,
  ParsedPppcApp,
  MobileconfigParseError,
} from "@/services/mobileconfigPppcParser";
import { PppcChoicesMap, PppcIdentifierType, getPppcServiceDef } from "@/types/pppc";
import { validatePppcChoices } from "@/services/pppcValidation";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB — .mobileconfig files are small

type DiffKind = "added" | "removed" | "unchanged";
interface DiffRow {
  kind: DiffKind;
  text: string;
}

/**
 * Line-level diff using a longest-common-subsequence walk.
 * Code requirement strings are short (typically <30 lines), so the O(n*m)
 * table is fine and avoids pulling in a diff library.
 */
const diffLines = (before: string, after: string): DiffRow[] => {
  const a = (before ?? "").split(/\r?\n/);
  const b = (after ?? "").split(/\r?\n/);
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      rows.push({ kind: "unchanged", text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      rows.push({ kind: "removed", text: a[i] });
      i++;
    } else {
      rows.push({ kind: "added", text: b[j] });
      j++;
    }
  }
  while (i < n) rows.push({ kind: "removed", text: a[i++] });
  while (j < m) rows.push({ kind: "added", text: b[j++] });
  return rows;
};

export interface PppcImportResult {
  identifier: string;
  identifierType: PppcIdentifierType;
  /** Final code requirement to apply (already conflict-resolved). */
  codeRequirement: string;
  /** Merged choices to apply (conflicts already resolved per "keep existing"). */
  choices: PppcChoicesMap;
  /** Source app's label, if any. */
  sourceLabel?: string;
  /** Counts for the success toast. */
  addedServiceCount: number;
  skippedConflictCount: number;
  /** Whether the imported code requirement replaced the form's value. */
  codeRequirementReplaced: boolean;
}

interface PppcImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current form choices — used to compute "added vs skipped (existing)". */
  currentChoices: PppcChoicesMap;
  /** Current code requirement; controls the conflict-warning step. */
  currentCodeRequirement: string;
  /** Optional bundle ID to auto-select a matching app from the file. */
  preferIdentifier?: string;
  onImport: (result: PppcImportResult) => void;
}

type Stage = "pick-file" | "validate" | "pick-app" | "code-conflict";

interface FileMeta {
  name: string;
  sizeBytes: number;
}

interface ValidationCheck {
  label: string;
  status: "pass" | "warn" | "fail";
  detail?: string;
}

/** Phase ordering helper — true if `current` is at or past `target`. */
const PHASE_ORDER = ["idle", "read", "parse", "validate", "done"] as const;
type PhaseName = (typeof PHASE_ORDER)[number];
const progressGte = (current: PhaseName, target: PhaseName): boolean =>
  PHASE_ORDER.indexOf(current) >= PHASE_ORDER.indexOf(target);

const PhaseStep = ({
  label,
  active,
  done,
}: {
  label: string;
  active: boolean;
  done: boolean;
}) => (
  <span
    className={cn(
      "flex items-center gap-1 transition-colors",
      done && "text-success",
      active && !done && "text-primary font-medium",
      !active && !done && "text-muted-foreground",
    )}
  >
    {done ? (
      <CheckCircle2 className="h-3 w-3" />
    ) : active ? (
      <Loader2 className="h-3 w-3 animate-spin" />
    ) : (
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
    )}
    {label}
  </span>
);

const PhaseDot = ({ done }: { done: boolean }) => (
  <span
    className={cn(
      "h-px w-3 transition-colors",
      done ? "bg-success" : "bg-muted-foreground/30",
    )}
  />
);

/**
 * Bulk import dialog for .mobileconfig templates.
 *
 * Flow:
 *   1. Pick file → parse with browser DOMParser.
 *   2. Pick app (always shown so admin sees what's in the file).
 *   3. If the picked entry's CodeRequirement differs from the form's, ask
 *      whether to keep current or replace. Otherwise skip straight to import.
 *
 * Merge rule: "Keep existing, add new" — services already configured in the
 * form are preserved; only new TCC keys from the file are added.
 */
export const PppcImportDialog = ({
  open,
  onOpenChange,
  currentChoices,
  currentCodeRequirement,
  preferIdentifier,
  onImport,
}: PppcImportDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>("pick-file");
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [profileName, setProfileName] = useState<string | undefined>();
  const [apps, setApps] = useState<ParsedPppcApp[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [replaceCodeReq, setReplaceCodeReq] = useState<boolean>(false);
  const [fileMeta, setFileMeta] = useState<FileMeta | null>(null);
  const [checks, setChecks] = useState<ValidationCheck[]>([]);
  // Counter (not boolean) to survive dragenter on child elements firing dragleave on parent.
  const [dragDepth, setDragDepth] = useState(0);
  const [dragInvalid, setDragInvalid] = useState(false);
  const dragActive = dragDepth > 0;
  // Progress reporting during file read / parse / validate.
  type ParsePhase = "idle" | "read" | "parse" | "validate" | "done";
  const [phase, setPhase] = useState<ParsePhase>("idle");
  const [progress, setProgress] = useState(0);

  // Reset everything when the dialog closes.
  useEffect(() => {
    if (!open) {
      setStage("pick-file");
      setParsing(false);
      setError(null);
      setWarnings([]);
      setProfileName(undefined);
      setApps([]);
      setSelectedAppId(null);
      setReplaceCodeReq(false);
      setFileMeta(null);
      setChecks([]);
      setDragDepth(0);
      setDragInvalid(false);
      setPhase("idle");
      setProgress(0);
    }
  }, [open]);

  /**
   * Read a file as text using FileReader so we can report true byte-level
   * progress for the read phase. Falls back to file.text() if FileReader is
   * unavailable for any reason.
   */
  const readFileWithProgress = (
    file: File,
    onProgress: (pct: number) => void,
  ): Promise<string> =>
    new Promise((resolve, reject) => {
      if (typeof FileReader === "undefined") {
        file.text().then(resolve, reject);
        return;
      }
      const reader = new FileReader();
      reader.onprogress = (ev) => {
        if (ev.lengthComputable) {
          // Map file-read progress (0..1) into the read phase (0..25%).
          onProgress(Math.min(25, (ev.loaded / ev.total) * 25));
        }
      };
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(reader.error ?? new Error("File read failed."));
      reader.onabort = () => reject(new Error("File read aborted."));
      reader.readAsText(file);
    });

  /** Yield to the browser so the progress UI can repaint between phases. */
  const yieldToPaint = () =>
    new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  const formatBytes = (n: number): string => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleFile = async (file: File) => {
    setError(null);
    setWarnings([]);
    setChecks([]);
    setFileMeta({ name: file.name, sizeBytes: file.size });

    if (file.size > MAX_FILE_BYTES) {
      setError("File is larger than 2 MB. .mobileconfig templates should be small.");
      return;
    }
    if (file.size === 0) {
      setError("File is empty.");
      return;
    }
    if (
      !/\.(mobileconfig|plist|xml)$/i.test(file.name) &&
      !file.type.includes("xml") &&
      !file.type.includes("plist")
    ) {
      setError(
        "Expected a .mobileconfig, .plist, or .xml file. Choose an unsigned profile export.",
      );
      return;
    }
    setParsing(true);
    setPhase("read");
    setProgress(2);
    try {
      // --- Phase 1: read file (0 → 25%) ---
      const text = await readFileWithProgress(file, (pct) =>
        setProgress(Math.max(2, pct)),
      );
      setProgress(25);
      await yieldToPaint();

      // --- Phase 2: parse XML/plist (25 → 60%) ---
      setPhase("parse");
      setProgress(35);
      await yieldToPaint();
      const result = parseMobileconfig(text);
      setProgress(60);
      await yieldToPaint();

      // --- Phase 3: validate TCC keys (60 → 90%) ---
      setPhase("validate");
      setProgress(70);
      await yieldToPaint();
      const aggregateChoices: PppcChoicesMap = {};
      for (const app of result.apps) {
        for (const [k, v] of Object.entries(app.choices)) {
          if (v === "allow" || v === "deny") aggregateChoices[k] = v;
        }
      }
      const issues = validatePppcChoices(aggregateChoices);
      if (issues.length > 0) {
        setError(
          `File contains ${issues.length} invalid TCC service key${issues.length === 1 ? "" : "s"}: ${issues
            .slice(0, 3)
            .map((i) => i.key)
            .join(", ")}${issues.length > 3 ? "…" : ""}. Aborting import.`,
        );
        setParsing(false);
        setPhase("idle");
        setProgress(0);
        return;
      }
      setProgress(90);

      setApps(result.apps);
      setProfileName(result.profileName);
      setWarnings(result.warnings);

      // Prefer an app matching the form's bundle ID if one is provided.
      const match = preferIdentifier
        ? result.apps.find(
            (a) =>
              a.identifierType === "bundleID" &&
              a.identifier.toLowerCase() === preferIdentifier.toLowerCase(),
          )
        : undefined;
      setSelectedAppId(match?.id ?? result.apps[0]?.id ?? null);

      // Build the validation checklist shown on the Validate stage.
      const totalServices = result.apps.reduce((n, a) => n + a.serviceCount, 0);
      const nextChecks: ValidationCheck[] = [
        {
          label: "File size and extension",
          status: "pass",
          detail: `${formatBytes(file.size)} · ${file.name.split(".").pop()?.toLowerCase() ?? "unknown"}`,
        },
        { label: "Well-formed XML plist", status: "pass" },
        { label: "Unsigned profile (parseable)", status: "pass" },
        {
          label: "TCC payload(s) present",
          status: "pass",
          detail: `${result.apps.length} app${result.apps.length === 1 ? "" : "s"} · ${totalServices} service${totalServices === 1 ? "" : "s"}`,
        },
        {
          label: "All TCC service keys are valid",
          status: "pass",
          detail: "No malformed custom keys detected",
        },
        {
          label: "Parser warnings",
          status: result.warnings.length === 0 ? "pass" : "warn",
          detail:
            result.warnings.length === 0
              ? "None"
              : `${result.warnings.length} non-fatal issue${result.warnings.length === 1 ? "" : "s"} — review before continuing`,
        },
      ];
      setChecks(nextChecks);

      // --- Done (100%) ---
      setPhase("done");
      setProgress(100);
      await yieldToPaint();
      setStage("validate");
    } catch (err) {
      const msg =
        err instanceof MobileconfigParseError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to parse file.";
      setError(msg);
      setPhase("idle");
      setProgress(0);
    } finally {
      setParsing(false);
    }
  };

  const handlePickFromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so the same file can be re-picked after an error.
    e.target.value = "";
  };

  // True if the drag payload claims to contain at least one file.
  const dragHasFile = (e: React.DragEvent): boolean => {
    const items = e.dataTransfer?.items;
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") return true;
      }
      return false;
    }
    // Some browsers (Safari) don't expose items during dragenter; fall back to types.
    return Array.from(e.dataTransfer?.types ?? []).includes("Files");
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFile(e)) return;
    e.preventDefault();
    setDragDepth((d) => d + 1);
    // Reject obvious non-mobileconfig drags up-front when we know the type.
    const item = e.dataTransfer.items?.[0];
    if (item && item.kind === "file" && item.type) {
      const ok =
        item.type.includes("xml") ||
        item.type.includes("plist") ||
        item.type === "" || // many browsers report empty for .mobileconfig
        item.type === "application/octet-stream";
      setDragInvalid(!ok);
    } else {
      setDragInvalid(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFile(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = dragInvalid ? "none" : "copy";
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragDepth(0);
    setDragInvalid(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (e.dataTransfer.files.length > 1) {
      setError("Drop a single .mobileconfig file at a time.");
      return;
    }
    handleFile(file);
  };

  const selectedApp = apps.find((a) => a.id === selectedAppId) ?? null;

  // --- Stage transitions ---

  const proceedFromPickApp = () => {
    if (!selectedApp) return;
    const trimmedCurrent = currentCodeRequirement.trim();
    const trimmedFile = selectedApp.codeRequirement.trim();
    const codeReqDiffers =
      trimmedCurrent.length > 0 &&
      trimmedFile.length > 0 &&
      trimmedCurrent !== trimmedFile;
    if (codeReqDiffers) {
      setReplaceCodeReq(false);
      setStage("code-conflict");
      return;
    }
    finalizeImport(selectedApp, trimmedFile.length > 0 && trimmedCurrent.length === 0);
  };

  const finalizeImport = (
    app: ParsedPppcApp,
    codeRequirementReplaced: boolean,
  ) => {
    // Merge with "keep existing, add new"
    const merged: PppcChoicesMap = { ...currentChoices };
    let added = 0;
    let skipped = 0;
    for (const [k, v] of Object.entries(app.choices)) {
      if (v !== "allow" && v !== "deny") continue;
      if (merged[k] === "allow" || merged[k] === "deny") {
        skipped += 1;
        continue;
      }
      merged[k] = v;
      added += 1;
    }

    const finalCodeReq = codeRequirementReplaced
      ? app.codeRequirement
      : currentCodeRequirement || app.codeRequirement;

    onImport({
      identifier: app.identifier,
      identifierType: app.identifierType,
      codeRequirement: finalCodeReq,
      choices: merged,
      sourceLabel: app.sourceLabel,
      addedServiceCount: added,
      skippedConflictCount: skipped,
      codeRequirementReplaced,
    });

    if (added === 0 && skipped > 0) {
      toast.info(
        `All ${skipped} service${skipped === 1 ? "" : "s"} from the file were already configured. Nothing changed.`,
      );
    } else {
      toast.success(
        `Imported ${added} service${added === 1 ? "" : "s"}${skipped > 0 ? ` (${skipped} kept as-is)` : ""}.`,
      );
    }
    onOpenChange(false);
  };

  const confirmCodeConflict = () => {
    if (!selectedApp) return;
    finalizeImport(selectedApp, replaceCodeReq);
  };

  // --- UI per stage ---

  const renderPickFile = () => (
    <div className="space-y-4">
      <div
        role="button"
        tabIndex={0}
        aria-label="Drop a .mobileconfig file here, or press Enter to browse"
        onClick={() => !parsing && fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !parsing) {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer outline-none",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          parsing && "border-primary/40 bg-primary/5 cursor-wait",
          !parsing && !dragActive && "border-muted-foreground/20 hover:border-primary/40 hover:bg-muted/30",
          !parsing && dragActive && !dragInvalid && "border-primary bg-primary/10 scale-[1.01]",
          !parsing && dragActive && dragInvalid && "border-destructive bg-destructive/10",
        )}
      >
        {parsing ? (
          <FileText className="mx-auto h-10 w-10 text-primary mb-3" />
        ) : (
          <>
            {dragActive && !dragInvalid && (
              <FileText className="mx-auto h-10 w-10 text-primary mb-3 animate-in fade-in zoom-in-95" />
            )}
            {dragActive && dragInvalid && (
              <XCircle className="mx-auto h-10 w-10 text-destructive mb-3" />
            )}
            {!dragActive && (
              <FileUp className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
            )}
          </>
        )}

        {parsing ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-primary truncate">
              {fileMeta?.name ?? "Processing file…"}
            </p>
            <Progress value={progress} className="h-1.5 mx-auto max-w-sm" />
            <div className="flex items-center justify-center gap-3 text-xs">
              <PhaseStep label="Read" active={phase === "read"} done={progressGte(phase, "parse")} />
              <PhaseDot done={progressGte(phase, "parse")} />
              <PhaseStep label="Parse" active={phase === "parse"} done={progressGte(phase, "validate")} />
              <PhaseDot done={progressGte(phase, "validate")} />
              <PhaseStep label="Validate" active={phase === "validate"} done={progressGte(phase, "done")} />
              <PhaseDot done={phase === "done"} />
              <PhaseStep label="Done" active={phase === "done"} done={phase === "done"} />
            </div>
            <p className="text-[11px] text-muted-foreground tabular-nums">
              {Math.round(progress)}%
              {fileMeta && phase === "read" && (
                <> · {formatBytes(Math.round((progress / 25) * fileMeta.sizeBytes))} of {formatBytes(fileMeta.sizeBytes)}</>
              )}
            </p>
          </div>
        ) : (
          <>
            <p className={cn(
              "text-sm font-medium",
              dragActive && !dragInvalid && "text-primary",
              dragActive && dragInvalid && "text-destructive",
            )}>
              {dragActive
                ? dragInvalid
                  ? "That file type isn't supported"
                  : "Release to upload"
                : "Drop a .mobileconfig file here"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {dragActive && dragInvalid
                ? "Only .mobileconfig, .plist, or .xml files are accepted."
                : "or click to browse — unsigned XML profiles only, max 2 MB."}
            </p>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Choose file
            </Button>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".mobileconfig,.plist,.xml,application/xml,text/xml"
          className="hidden"
          onChange={handlePickFromInput}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Cannot import</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Only unsigned .mobileconfig templates are supported. If your profile
          is signed (PKCS#7), open it in Apple Configurator or Profile Manager
          and export an unsigned copy first.
        </AlertDescription>
      </Alert>
    </div>
  );

  const renderValidate = () => {
    const totalServices = apps.reduce((n, a) => n + a.serviceCount, 0);
    const hasFailure = checks.some((c) => c.status === "fail");
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3 p-3 rounded-md border bg-muted/40">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{fileMeta?.name}</p>
              <p className="text-xs text-muted-foreground">
                {fileMeta ? formatBytes(fileMeta.sizeBytes) : ""}
                {profileName && (
                  <>
                    {" · "}
                    <span className="text-foreground">{profileName}</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <Badge variant="secondary" className="gap-1 shrink-0">
            <ShieldCheck className="h-3 w-3" />
            {hasFailure ? "Failed" : "Validated"}
          </Badge>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Validation checks</Label>
          <ul className="mt-2 space-y-1.5">
            {checks.map((c, i) => {
              const Icon =
                c.status === "pass"
                  ? CheckCircle2
                  : c.status === "warn"
                    ? AlertTriangle
                    : XCircle;
              const tone =
                c.status === "pass"
                  ? "text-success"
                  : c.status === "warn"
                    ? "text-warning"
                    : "text-destructive";
              return (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Icon className={cn("h-4 w-4 shrink-0 mt-0.5", tone)} />
                  <div className="min-w-0 flex-1">
                    <span>{c.label}</span>
                    {c.detail && (
                      <span className="block text-xs text-muted-foreground">
                        {c.detail}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md border p-2">
            <p className="text-lg font-semibold">{apps.length}</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
              App{apps.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="rounded-md border p-2">
            <p className="text-lg font-semibold">{totalServices}</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
              Service{totalServices === 1 ? "" : "s"}
            </p>
          </div>
          <div className="rounded-md border p-2">
            <p className="text-lg font-semibold">{warnings.length}</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
              Warning{warnings.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        {warnings.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="text-sm">
              {warnings.length} non-fatal warning{warnings.length === 1 ? "" : "s"}
            </AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5 space-y-0.5 text-xs mt-1">
                {warnings.slice(0, 5).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
                {warnings.length > 5 && (
                  <li className="text-muted-foreground">
                    …and {warnings.length - 5} more
                  </li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  const renderPickApp = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="text-muted-foreground">
          {profileName ? (
            <>
              Detected profile: <span className="font-medium text-foreground">{profileName}</span>
            </>
          ) : (
            "TCC payloads detected in this file:"
          )}
        </div>
        <Badge variant="secondary">{apps.length} app{apps.length === 1 ? "" : "s"}</Badge>
      </div>

      <ScrollArea className="h-[320px] pr-2">
        <div className="space-y-2">
          {apps.map((app) => {
            const selected = selectedAppId === app.id;
            const matchesPrefer =
              !!preferIdentifier &&
              app.identifierType === "bundleID" &&
              app.identifier.toLowerCase() === preferIdentifier.toLowerCase();
            const knownLabels = Object.keys(app.choices)
              .slice(0, 4)
              .map((k) => getPppcServiceDef(k).label);
            return (
              <button
                key={app.id}
                type="button"
                onClick={() => setSelectedAppId(app.id)}
                className={cn(
                  "w-full text-left p-3 rounded-md border transition-colors",
                  selected
                    ? "border-primary bg-primary/5"
                    : "hover:border-primary/40",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">
                        {app.sourceLabel ?? app.identifier}
                      </span>
                      {matchesPrefer && (
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Matches form
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {app.identifierType === "path" ? "Path" : "Bundle ID"}
                      </Badge>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground truncate">
                      {app.identifier}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {app.serviceCount} service{app.serviceCount === 1 ? "" : "s"}
                      {knownLabels.length > 0 && (
                        <>
                          {" — "}
                          {knownLabels.join(", ")}
                          {app.serviceCount > knownLabels.length && "…"}
                        </>
                      )}
                    </p>
                  </div>
                  {selected && <Badge variant="secondary">Selected</Badge>}
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>

      {warnings.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{warnings.length} warning{warnings.length === 1 ? "" : "s"}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-5 space-y-0.5 text-xs mt-1">
              {warnings.slice(0, 5).map((w, i) => (
                <li key={i}>{w}</li>
              ))}
              {warnings.length > 5 && (
                <li className="text-muted-foreground">
                  …and {warnings.length - 5} more
                </li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderCodeConflict = () => {
    if (!selectedApp) return null;
    const choice: "keep" | "replace" = replaceCodeReq ? "replace" : "keep";
    const diff = diffLines(currentCodeRequirement, selectedApp.codeRequirement);
    const addedCount = diff.filter((r) => r.kind === "added").length;
    const removedCount = diff.filter((r) => r.kind === "removed").length;
    return (
      <div className="space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Code Requirement differs</AlertTitle>
          <AlertDescription className="text-xs">
            The form already has a Code Requirement set, and the imported file
            has a different one. Pick which value to apply.
          </AlertDescription>
        </Alert>

        <div className="rounded-md border overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b text-xs">
            <span className="font-medium">Diff: current → file</span>
            <div className="flex items-center gap-3 text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm bg-success" />
                {addedCount} added
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-sm bg-destructive" />
                {removedCount} removed
              </span>
            </div>
          </div>
          <ScrollArea className="max-h-56">
            <pre className="text-[11px] font-mono leading-relaxed">
              {diff.map((row, i) => {
                const tone =
                  row.kind === "added"
                    ? "bg-success-muted text-success-muted-foreground"
                    : row.kind === "removed"
                      ? "bg-destructive/10 text-destructive"
                      : "text-muted-foreground";
                const sigil =
                  row.kind === "added" ? "+" : row.kind === "removed" ? "−" : " ";
                return (
                  <div
                    key={i}
                    className={cn(
                      "grid grid-cols-[1.25rem_1fr] gap-2 px-3 py-0.5",
                      tone,
                    )}
                  >
                    <span className="select-none text-right opacity-70">
                      {sigil}
                    </span>
                    <span className="whitespace-pre-wrap break-all">
                      {row.text || "\u00A0"}
                    </span>
                  </div>
                );
              })}
            </pre>
          </ScrollArea>
        </div>

        <RadioGroup
          value={choice}
          onValueChange={(v) => setReplaceCodeReq(v === "replace")}
          className="space-y-2"
        >
          <Label
            htmlFor="cr-keep"
            className={cn(
              "flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors",
              choice === "keep"
                ? "border-primary bg-primary/5"
                : "hover:border-primary/40",
            )}
          >
            <RadioGroupItem id="cr-keep" value="keep" className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Keep current</span>
                <Badge variant="outline" className="text-[10px]">
                  From form
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Leave the Code Requirement you already entered unchanged.
              </p>
              <pre className="mt-2 text-[11px] font-mono bg-muted rounded p-2 max-h-20 overflow-auto whitespace-pre-wrap break-all">
                {currentCodeRequirement || "\u00A0"}
              </pre>
            </div>
          </Label>

          <Label
            htmlFor="cr-replace"
            className={cn(
              "flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors",
              choice === "replace"
                ? "border-primary bg-primary/5"
                : "hover:border-primary/40",
            )}
          >
            <RadioGroupItem id="cr-replace" value="replace" className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Replace with file value</span>
                <Badge variant="outline" className="text-[10px]">
                  From .mobileconfig
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Overwrite the form's Code Requirement with the value from the
                imported profile.
              </p>
              <pre className="mt-2 text-[11px] font-mono bg-muted rounded p-2 max-h-20 overflow-auto whitespace-pre-wrap break-all">
                {selectedApp.codeRequirement || "\u00A0"}
              </pre>
            </div>
          </Label>
        </RadioGroup>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import from .mobileconfig</DialogTitle>
          <DialogDescription>
            {stage === "pick-file" &&
              "Upload an unsigned configuration profile to pre-populate managed PPPC services."}
            {stage === "validate" &&
              "Review the validation results before applying any changes to the form."}
            {stage === "pick-app" &&
              "Pick which app's TCC entries to import. Existing services in the form are preserved."}
            {stage === "code-conflict" &&
              "Resolve a Code Requirement difference before applying the import."}
          </DialogDescription>
        </DialogHeader>

        {stage === "pick-file" && renderPickFile()}
        {stage === "validate" && renderValidate()}
        {stage === "pick-app" && renderPickApp()}
        {stage === "code-conflict" && renderCodeConflict()}

        <DialogFooter>
          {stage === "pick-file" && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
          {stage === "validate" && (
            <>
              <Button variant="outline" onClick={() => setStage("pick-file")}>
                Choose different file
              </Button>
              <Button
                onClick={() => setStage("pick-app")}
                disabled={checks.some((c) => c.status === "fail")}
              >
                Continue
              </Button>
            </>
          )}
          {stage === "pick-app" && (
            <>
              <Button variant="outline" onClick={() => setStage("validate")}>
                Back
              </Button>
              <Button onClick={proceedFromPickApp} disabled={!selectedApp}>
                Continue
              </Button>
            </>
          )}
          {stage === "code-conflict" && (
            <>
              <Button variant="outline" onClick={() => setStage("pick-app")}>
                Back
              </Button>
              <Button onClick={confirmCodeConflict}>Apply import</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
